import {
  cancelScheduledTransactionService,
  getScheduledTransactionDetailsService,
  getScheduledTransactionListService,
  getTransactionDetailsService,
  getTransactionListService,
} from '@/services/transactions';
import {
  queryKeys,
  ScheduledTransactionsListQueryParams,
  TransactionsListQueryParams,
} from '@/types/transactions';
import { convertObjectValuesToString } from '@/utils/responseConvertors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useGetTransactionList = (
  bankAccountId: string,
  queryParams: TransactionsListQueryParams,
) => {
  const params = new URLSearchParams(
    convertObjectValuesToString(queryParams),
  ).toString();

  const { data, isPending, isSuccess } = useQuery({
    queryKey: [queryKeys.list, `${params}${bankAccountId}`],
    queryFn: () => getTransactionListService(bankAccountId, params),
    enabled: !!bankAccountId,
  });

  return {
    isSuccess,
    transactionList: data?.records || [],
    totalRecordCount: data?.totalRecordCount || 0,
    isTransactionListPending: isPending,
  };
};

export const useGetTransactionDetails = (
  bankAccountUuid: string,
  transactionId: string,
) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.list, transactionId],
    queryFn: () => getTransactionDetailsService(bankAccountUuid, transactionId),
    enabled: !!bankAccountUuid || !!transactionId,
  });

  return {
    transactionDetails: data,
    isTransactionDetailsPending: isPending,
  };
};

export const useGetScheduledTransactionList = (
  bankAccountId: string,
  queryParams: ScheduledTransactionsListQueryParams,
) => {
  const params = new URLSearchParams(
    convertObjectValuesToString(queryParams),
  ).toString();

  const { data, isPending, isSuccess } = useQuery({
    queryKey: [queryKeys.scheduledList, `${params}${bankAccountId}`],
    queryFn: () => getScheduledTransactionListService(bankAccountId, params),
    enabled: !!bankAccountId,
  });

  return {
    isSuccess,
    scheduledTransactionsList: data?.scheduledTransactions.records || [],
    aggregateInfo: data?.aggregateInfo,
    totalRecordCount: data?.scheduledTransactions.totalRecordCount || 0,
    isScheduledTransactionListPending: isPending,
  };
};

export const useGetScheduledTransactionDetails = (transactionId: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.scheduledDetails, transactionId],
    queryFn: () => getScheduledTransactionDetailsService(transactionId),
    enabled: !!transactionId,
  });

  return {
    scheduledTransactionDetails: data,
    isScheduledTransactionDetailsPending: isPending,
  };
};

export const useCancelScheduledTransaction = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (transactionId: string) =>
      cancelScheduledTransactionService(transactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.scheduledList],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.scheduledDetails],
      });
    },
  });

  return {
    cancelScheduledTransactionPending: mutateAsync,
    isCancelScheduledTransactionPending: isPending,
  };
};
